// Shared atoms and helpers used by all landing-page template renderers.
//
// Theming: PreviewFrame is the LandingPageThemeProvider for templates.
// It accepts a resolved `theme` ({ primaryColor, headingFont, bodyFont,
// buttonRadius }) and exposes the theme primitives as CSS variables on
// the inner wrapper:
//
//   --lp-accent       primary brand color (templates may still override
//                     for their template-defined section accents)
//   --lp-accent-soft  rgba tint of the accent for soft backgrounds
//   --lp-heading-font heading font stack
//   --lp-body-font    body font stack (also applied as fontFamily root)
//   --lp-radius       button corner radius
//
// Templates that need template-defined section colors (e.g.,
// CampaignPromo's yellow header band) keep those hardcodes — only the
// global brand primitives flow through this provider.
import React from 'react';
import {
  PiStar, PiStarFill,
} from 'react-icons/pi';
import { resolveFontStack } from '../../../utils/landingPageTheme';

// Deterministic avatar bg color from a seed (name/initials).
export const AVATAR_COLORS = ['#FFD4A8', '#A8D4FF', '#C8FFC8', '#FFC8E1', '#E1C8FF', '#FFEFB0', '#B0EAFF'];
export function avatarBg(seed) {
  const s = String(seed || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
export function initialsFor(r, i = 0) {
  if (r.initials) return r.initials;
  const name = r.name || `R${i}`;
  return name.split(' ').map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

// Interpolate {token} placeholders in editable copy (e.g. 'Get all {count} for {price}').
// Missing/null tokens resolve to '' so a partial map never leaks a raw '{token}'.
export function fillTokens(str, map) {
  return String(str ?? '').replace(/\{(\w+)\}/g, (m, k) => (map && map[k] != null ? String(map[k]) : ''));
}

// Derive a softer tint from the brand color for backgrounds / borders.
export function hexToSoft(hex, alpha = 0.08) {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#') || hex.length < 7) {
    return `rgba(134, 97, 255, ${alpha})`;
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Default theme used when a template is rendered without a theme prop
// (e.g., legacy callers, isolated previews).
const DEFAULT_THEME = {
  primaryColor: '#8661FF',
  secondaryColor: '#1F1B33',
  pageBackgroundColor: '#ffffff',
  headingFont: 'system',
  bodyFont: 'system',
  buttonRadius: 6,
};

export function PreviewFrame({
  viewport = 'desktop', children, accent, theme, sections,
  editable = false, onSectionClick,
}) {
  const widthByVp = { desktop: '100%', tablet: 768, mobile: 390 };
  const w = widthByVp[viewport] || '100%';
  const t = theme || DEFAULT_THEME;
  const headingStack = resolveFontStack(t.headingFont, 'heading');
  const bodyStack    = resolveFontStack(t.bodyFont,    'body');
  const pageBg       = t.pageBackgroundColor || '#ffffff';
  const secondary    = t.secondaryColor      || '#1F1B33';
  // Per-template `accent` (passed by the template) takes precedence for the
  // CSS var so template-defined section colors keep working. Brand primary
  // is exposed as --lp-brand for places that want it explicitly.
  const cssVars = {
    '--lp-accent':       accent || t.primaryColor,
    '--lp-accent-soft':  hexToSoft(accent || t.primaryColor),
    '--lp-brand':        t.primaryColor,
    '--lp-brand-soft':   hexToSoft(t.primaryColor),
    '--lp-secondary':    secondary,
    '--lp-page-bg':      pageBg,
    '--lp-heading-font': headingStack,
    '--lp-body-font':    bodyStack,
    '--lp-radius':       `${t.buttonRadius}px`,
  };
  // Unique scope id so injected per-section CSS only targets this preview.
  const scopeId = React.useMemo(
    () => `lp-${Math.random().toString(36).slice(2, 9)}`,
    []
  );

  // Click delegation: when editable, any click inside the preview walks
  // up to the nearest [data-lp-section] and reports its id. Each section
  // is also given cursor:pointer + a hover outline via injected CSS so
  // users feel the affordance. Buttons/links/inputs inside the preview
  // are non-interactive in this mode (clicks do not navigate or focus
  // them — they just route to the section editor).
  const handleClick = React.useCallback((e) => {
    if (!editable || !onSectionClick) return;
    const target = e.target.closest('[data-lp-section]');
    if (!target) return;
    e.preventDefault();
    e.stopPropagation();
    const id = target.getAttribute('data-lp-section');
    if (id) onSectionClick(id);
  }, [editable, onSectionClick]);

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        background: 'var(--bg-overlays)',
        borderRadius: 8,
        padding: viewport === 'desktop' ? 0 : 16,
        minHeight: 520,
        overflow: 'hidden',
      }}
    >
      <div
        id={scopeId}
        onClick={editable ? handleClick : undefined}
        style={{
          ...cssVars,
          width: w,
          maxWidth: '100%',
          background: pageBg,
          borderRadius: viewport === 'desktop' ? 0 : 12,
          overflow: 'hidden',
          boxShadow: viewport === 'desktop' ? 'none' : '0 4px 24px rgba(0,0,0,0.06)',
          fontFamily: bodyStack,
          color: '#1a1a1a',
          position: 'relative',
        }}
      >
        <SectionOverridesStyle scopeId={scopeId} sections={sections} />
        {editable && <EditableOverlayStyle scopeId={scopeId} />}
        {children}
      </div>
    </div>
  );
}

// Hover/click affordance for the editable preview. Outlines each section
// on hover, dims interactive children so they look "framed" rather than
// clickable, and adds a small "Edit" badge in the top-right of the
// hovered section.
function EditableOverlayStyle({ scopeId }) {
  const css = `
    #${scopeId} [data-lp-section] { cursor: pointer; position: relative; }
    #${scopeId} [data-lp-section] * { pointer-events: none; }
    #${scopeId} [data-lp-section]:hover {
      outline: 2px dashed var(--lp-brand, #8661FF);
      outline-offset: -2px;
    }
    #${scopeId} [data-lp-section]:hover::after {
      content: 'Edit';
      position: absolute;
      top: 6px; right: 6px;
      background: var(--lp-brand, #8661FF);
      color: #fff;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      font-size: 10px; font-weight: 700;
      letter-spacing: 0.5px; text-transform: uppercase;
      padding: 3px 7px; border-radius: 3px;
      pointer-events: none;
      z-index: 10;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    }
  `;
  // eslint-disable-next-line react/no-danger
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}

// Emits a <style> tag with per-section overrides for background + text styles.
// Sections shape (per id): { __bg?: { color, imageUrl, size, position, overlayOpacity },
//                            __styles?: { headline?: {...}, subheadline?: {...}, body?: {...} } }
// CSS targets `[data-lp-section="<id>"]` inside the scoped preview wrapper.
function SectionOverridesStyle({ scopeId, sections }) {
  if (!sections || typeof sections !== 'object') return null;
  const rules = [];
  const cssEscape = (s) => String(s).replace(/"/g, '\\"');
  Object.entries(sections).forEach(([id, content]) => {
    if (!content || typeof content !== 'object') return;
    const sel = `#${scopeId} [data-lp-section="${cssEscape(id)}"]`;
    const bg = content.__bg;
    if (bg && (bg.color || bg.imageUrl)) {
      const layers = [];
      if (bg.imageUrl) {
        const overlay = Math.max(0, Math.min(0.8, Number(bg.overlayOpacity) || 0));
        if (overlay > 0) {
          layers.push(`linear-gradient(rgba(0,0,0,${overlay}),rgba(0,0,0,${overlay}))`);
        }
        layers.push(`url("${cssEscape(bg.imageUrl)}")`);
      }
      const props = [];
      if (bg.color) props.push(`background-color: ${bg.color} !important`);
      if (layers.length) {
        props.push(`background-image: ${layers.join(', ')} !important`);
        props.push(`background-size: ${bg.size || 'cover'} !important`);
        props.push(`background-position: ${bg.position || 'center center'} !important`);
        props.push(`background-repeat: no-repeat !important`);
      }
      if (props.length) rules.push(`${sel} { ${props.join('; ')}; }`);
    }
    const styles = content.__styles;
    if (styles && typeof styles === 'object') {
      // Map field name → CSS selector inside the section.
      const map = {
        headline:    'h1, h2',
        subheadline: 'h2 + p, h1 + p, .lp-subheadline, p:first-of-type',
        body:        'p',
        ctaLabel:    'button',
      };
      Object.entries(styles).forEach(([field, s]) => {
        if (!s || typeof s !== 'object') return;
        // Mapped fields also match [data-lp-field] so non-heading elements
        // (e.g. span/div headlines) are styleable. Every comma-separated leg
        // must be scoped under the section selector — otherwise the tail of
        // the list (e.g. the bare `h2` in 'h1, h2') leaks page-wide.
        const fieldSel = `[data-lp-field="${cssEscape(field)}"]`;
        const childSel = map[field] ? `${map[field]}, ${fieldSel}` : fieldSel;
        const scoped = childSel.split(',').map((leg) => `${sel} ${leg.trim()}`).join(', ');
        const props = [];
        if (s.fontSize)   props.push(`font-size: ${parseInt(s.fontSize, 10)}px !important`);
        if (s.fontWeight) props.push(`font-weight: ${parseInt(s.fontWeight, 10)} !important`);
        if (s.color)      props.push(`color: ${s.color} !important`);
        if (s.textAlign)  props.push(`text-align: ${s.textAlign} !important`);
        if (props.length) rules.push(`${scoped} { ${props.join('; ')}; }`);
      });
    }
  });
  if (!rules.length) return null;
  // eslint-disable-next-line react/no-danger
  return <style dangerouslySetInnerHTML={{ __html: rules.join('\n') }} />;
}


export function Btn({ children, color = 'var(--lp-accent)', big, full, textColor = '#fff', onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: color, color: textColor, border: 'none',
        padding: big ? '16px 32px' : '11px 22px',
        borderRadius: 'var(--lp-radius, 8px)',
        fontSize: big ? 16 : 13, fontWeight: 700,
        fontFamily: 'var(--lp-heading-font, inherit)',
        cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
        width: full ? '100%' : undefined,
        boxShadow: big ? '0 4px 14px rgba(0,0,0,0.12)' : 'none',
        letterSpacing: 0.2,
        textTransform: big ? 'none' : undefined,
        opacity: disabled ? 0.65 : 1,
      }}
    >
      {children}
    </button>
  );
}

export function Stars({ n = 5, size = 13 }) {
  // react-icons has no `weight` prop (that's @phosphor-icons/react API) —
  // filled and outline are separate components, and a fractional rating
  // (4.6) renders as a width-clipped filled star over the outline.
  const value = Math.max(0, Math.min(5, Number(n) || 0));
  return (
    <span style={{ display: 'inline-flex', gap: 1, color: '#FFB400', verticalAlign: 'middle' }}>
      {Array.from({ length: 5 }).map((_, i) => {
        const frac = Math.max(0, Math.min(1, value - i));
        if (frac >= 1) return <PiStarFill key={i} size={size} />;
        if (frac <= 0) return <PiStar key={i} size={size} />;
        return (
          <span key={i} style={{ position: 'relative', display: 'inline-flex', width: size, height: size }}>
            <PiStar size={size} />
            <span style={{
              position: 'absolute', top: 0, left: 0, height: '100%',
              width: `${Math.round(frac * 100)}%`, overflow: 'hidden', display: 'inline-flex',
            }}>
              <PiStarFill size={size} style={{ flexShrink: 0 }} />
            </span>
          </span>
        );
      })}
    </span>
  );
}

export function TrustBar({ items, dark }) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
      gap: 28, padding: '14px 24px',
      background: dark ? 'rgba(255,255,255,0.06)' : '#FAFAFC',
      borderTop: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #ECE8F5',
      borderBottom: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #ECE8F5',
      fontSize: 12, color: dark ? 'rgba(255,255,255,0.85)' : '#4a4458', fontWeight: 500,
    }}>
      {items.map(([Icon, label], i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon size={16} /> {label}
        </span>
      ))}
    </div>
  );
}

export function PressBar({ dark, logos, label = 'AS FEATURED IN' }) {
  // Press logos are a factual endorsement claim — only render real,
  // merchant-supplied logos. Never fabricate "AS FEATURED IN" outlets.
  const list = Array.isArray(logos) ? logos.filter((l) => l && String(l).trim()) : [];
  if (list.length === 0) return null;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 32,
      padding: '20px 24px',
      background: dark ? 'transparent' : '#fff',
    }}>
      {label ? (
        <span data-lp-field="pressLabel" style={{ fontSize: 11, color: dark ? 'rgba(255,255,255,0.5)' : '#9892a3', letterSpacing: 1.5, alignSelf: 'center' }}>
          {label}
        </span>
      ) : null}
      {list.map((l) => (
        <span key={l} style={{
          fontSize: 16, fontWeight: 800, letterSpacing: 1.5,
          color: dark ? 'rgba(255,255,255,0.6)' : '#9892a3',
          fontFamily: 'Georgia, serif',
        }}>{l}</span>
      ))}
    </div>
  );
}
